import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, errMsg, STATUSES, STATUS_META, itDate, download, TYPE_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowClockwise, Copy, Lightning, Printer, CheckCircle, FilePdf } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATI_CONTATORI = ["PRESENTE", "ASSENTE", "FERIE", "MALATTIA", "INFORTUNIO", "PERMESSO"];

export default function ManagerDashboard() {
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [department, setDepartment] = useState("all");
  const [departments, setDepartments] = useState([]);
  const [empStatus, setEmpStatus] = useState("active");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [salvato, setSalvato] = useState("");
  const [approvate, setApprovate] = useState(null);
  const [elenco, setElenco] = useState(null);
  const [elencoApprovate, setElencoApprovate] = useState(false);
  const [conferma, setConferma] = useState(null);
  const [turno, setTurno] = useState("");
  const timers = useRef({});

  useEffect(() => { api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/rollcall", { params: { day, department, status: empStatus } });
      setData(d);
      const nuovo = {};
      d.rows.forEach((r) => { nuovo[r.employee_id] = { status: r.status || "", notes: r.notes || "" }; });
      setDraft(nuovo);
      const { data: a } = await api.get("/requests/approved-on", { params: { day } });
      setApprovate(a);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [day, department, empStatus]);

  useEffect(() => { load(); }, [load]);

  const righe = data?.rows || [];
  const filtrate = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return righe;
    return righe.filter((r) => `${r.first_name} ${r.last_name} ${r.code} ${r.department}`
      .toLowerCase().includes(s));
  }, [righe, q]);

  // contatori calcolati sul posto: si aggiornano a ogni causale, senza ricaricare
  const conteggi = useMemo(() => {
    const c = Object.fromEntries(STATI_CONTATORI.map((s) => [s, 0]));
    const mancanti = [];
    righe.forEach((r) => {
      const st = draft[r.employee_id]?.status;
      if (!st) mancanti.push(r);
      else if (c[st] !== undefined) c[st] += 1;
    });
    return { c, mancanti };
  }, [righe, draft]);

  const completo = righe.length > 0 && conteggi.mancanti.length === 0;

  const elencoRighe = !elenco ? []
    : (elenco.key === "MISSING" ? conteggi.mancanti
      : righe.filter((r) => draft[r.employee_id]?.status === elenco.key));

  const set = (id, patch) => setDraft((p) => ({ ...p, [id]: { ...p[id], ...patch } }));

  const salva = useCallback(async (items, messaggio) => {
    if (!items.length) return;
    setSaving(true);
    try {
      await api.post("/attendance/bulk", { items });
      setSalvato(messaggio);
      setTimeout(() => setSalvato(""), 2500);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  }, []);

  const riga = (r, stato, note) => ({
    employee_id: r.employee_id, date: day, status: stato,
    entry_time: null, exit_time: null, break_minutes: 0, notes: note || null,
  });

  const setStatus = (r, stato) => {
    set(r.employee_id, { status: stato });
    salva([riga(r, stato, draft[r.employee_id]?.notes)], "Salvato");
  };

  const setNotes = (r, note) => {
    set(r.employee_id, { notes: note });
    const stato = draft[r.employee_id]?.status;
    if (!stato) return;
    clearTimeout(timers.current[r.employee_id]);
    timers.current[r.employee_id] = setTimeout(
      () => salva([riga(r, stato, note)], "Note salvate"), 1000);
  };

  const applica = (status, soloVuoti) => {
    const bersagli = soloVuoti ? filtrate.filter((r) => !draft[r.employee_id]?.status) : filtrate;
    if (!bersagli.length) { toast.info("Nessun dipendente da aggiornare"); setConferma(null); return; }
    const next = { ...draft };
    bersagli.forEach((r) => { next[r.employee_id] = { ...next[r.employee_id], status }; });
    setDraft(next);
    salva(bersagli.map((r) => riga(r, status, next[r.employee_id]?.notes)),
      `${bersagli.length} registrazioni salvate`);
    setConferma(null);
  };

  const applyAll = (status) => {
    const gia = filtrate.filter((r) => draft[r.employee_id]?.status);
    if (gia.length) { setConferma({ status, gia: gia.length, vuoti: filtrate.length - gia.length }); return; }
    applica(status, false);
  };

  const copiaTurno = async () => {
    const presenti = righe.filter((r) => draft[r.employee_id]?.status === "PRESENTE");
    if (!presenti.length) { toast.error("Nessun dipendente presente per la data selezionata"); return; }
    const testo = [
      `Domani ${itDate(day)} - INIZIO TURNO ALLE ORE 6.30 LAVORANO I SEGUENTI OPERATORI:`,
      ...presenti.map((r) => `${r.last_name} ${r.first_name}`.trim()),
    ].join("\n");
    try {
      await navigator.clipboard.writeText(testo);
      toast.success(`Lista turno copiata · ${presenti.length} operatori`);
    } catch { setTurno(testo); }
  };

  return (
    <div data-testid="manager-dashboard" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Appello Giornaliero</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Causali, note, richieste e certificati del giorno: ogni causale si salva da sola al clic
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          {saving && <span className="text-muted-foreground" data-testid="quick-saving">Salvataggio...</span>}
          {!saving && salvato && (
            <span className="font-semibold text-emerald-600" data-testid="quick-saved">{salvato}</span>
          )}
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card p-3 mobile-stack flex flex-wrap items-center gap-2">
        <Input type="date" value={day} onChange={(e) => setDay(e.target.value)}
          className="h-9 w-[170px] rounded-sm" data-testid="rollcall-date" />
        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger className="h-9 w-[170px] rounded-sm" data-testid="rollcall-department"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i reparti</SelectItem>
            {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={empStatus} onValueChange={setEmpStatus}>
          <SelectTrigger className="h-9 w-[180px] rounded-sm" data-testid="quick-emp-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Solo attivi</SelectItem>
            <SelectItem value="inactive">Solo non attivi</SelectItem>
            <SelectItem value="all">Attivi e non attivi</SelectItem>
          </SelectContent>
        </Select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca dipendente..."
          className="h-9 w-56 rounded-sm" data-testid="quick-search" />
        <Button variant="outline" size="sm" className="h-9 rounded-sm" onClick={load} data-testid="rollcall-refresh">
          <ArrowClockwise size={15} className="mr-1.5" /> Aggiorna
        </Button>
        <Button variant="secondary" size="sm" className="h-9 rounded-sm" data-testid="rollcall-today"
          onClick={() => setDay(new Date().toISOString().slice(0, 10))}>Oggi</Button>
        <Button variant="outline" size="sm" className="h-9 rounded-sm" data-testid="quick-print-daily"
          onClick={() => download(`/export/daily-pdf?day=${day}&department=${department}&status=${empStatus}`,
            `report_giornaliero_${day}.pdf`).catch((e) => toast.error(errMsg(e)))}>
          <Printer size={15} className="mr-1.5" /> Stampa giornata
        </Button>
        <Button variant="outline" size="sm" className="h-9 rounded-sm font-semibold" data-testid="rollcall-copy-shift"
          onClick={copiaTurno} title="Copia l'elenco dei presenti da incollare su WhatsApp">
          <Copy size={15} className="mr-1.5" /> Copia lista turno
        </Button>
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Lightning size={14} /> Applica a tutti
          </span>
          {STATUSES.map((s) => (
            <button key={s} data-testid={`apply-all-${s}`} onClick={() => applyAll(s)}
              className={`h-8 px-2 rounded-sm text-[11px] font-bold border border-border ${STATUS_META[s].cell}`}>
              {STATUS_META[s].short}
            </button>
          ))}
        </div>
      </div>

      {completo && (
        <div className="flex items-center gap-2 rounded-sm border border-emerald-500/50 bg-emerald-50 px-3 py-2
          text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" data-testid="daily-pdf-created">
          <CheckCircle size={20} weight="fill" />
          <span className="font-head text-base md:text-lg font-bold tracking-wide">PDF GIORNALIERO CREATO</span>
          <span className="text-xs">tutte le causali del {itDate(day)} sono complete</span>
          <Button size="sm" variant="outline" data-testid="daily-pdf-open"
            className="ml-auto h-8 rounded-sm border-emerald-600/60 font-semibold"
            onClick={() => download(`/export/daily-pdf?day=${day}&department=${department}&status=${empStatus}`,
              `presenze_${day}.pdf`).catch((e) => toast.error(errMsg(e)))}>
            <FilePdf size={15} className="mr-1.5" /> Apri PDF
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-8 gap-3">
        <button type="button" onClick={() => setElenco({ key: "MISSING", label: "Manca all'appello" })}
          className="text-left rounded-sm border border-destructive/40 bg-destructive/10 p-3 hover:bg-destructive/20 transition-colors duration-150"
          data-testid="stat-missing">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-destructive">Manca all'appello</div>
          <div className="mt-1 text-3xl font-head font-bold tabular-nums">
            {loading ? "—" : conteggi.mancanti.length}
          </div>
        </button>
        {STATI_CONTATORI.map((s) => (
          <button type="button" key={s} onClick={() => setElenco({ key: s, label: s })}
            className="text-left rounded-sm border border-border bg-card p-3 hover:bg-secondary/60 transition-colors duration-150"
            data-testid={`rollcall-${s}`}>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <span className={`h-2 w-2 rounded-full ${STATUS_META[s].dot}`} /> {s}
            </div>
            <div className="mt-1 text-3xl font-head font-bold tabular-nums">
              {loading ? "—" : conteggi.c[s]}
            </div>
          </button>
        ))}
        <button type="button" onClick={() => setElencoApprovate(true)}
          className="text-left rounded-sm border border-primary/40 bg-primary/10 p-3 hover:bg-primary/20 transition-colors duration-150"
          data-testid="stat-approved-requests">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">Richieste approvate</div>
          <div className="mt-1 text-3xl font-head font-bold tabular-nums">{approvate?.total ?? "—"}</div>
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Link to="/richieste" data-testid="link-requests"
          className="rounded-sm border border-border bg-card p-3.5 hover:bg-secondary/50 transition-colors duration-150">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Richieste in attesa</div>
          <div className="mt-1 flex items-end gap-2">
            <span className="text-2xl font-head font-bold tabular-nums" data-testid="pending-requests">{data?.pending_requests ?? "—"}</span>
            <span className="text-xs text-muted-foreground mb-1">ferie · permessi · 104</span>
          </div>
        </Link>
        <Link to="/certificati" data-testid="link-certificates"
          className="rounded-sm border border-border bg-card p-3.5 hover:bg-secondary/50 transition-colors duration-150">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Certificati di malattia da validare</div>
          <div className="mt-1 flex items-end gap-2">
            <span className="text-2xl font-head font-bold tabular-nums" data-testid="pending-certificates">{data?.pending_certificates ?? "—"}</span>
            <span className="text-xs text-muted-foreground mb-1">caricati dai dipendenti</span>
          </div>
        </Link>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-auto scroll-thin" style={{ maxHeight: "68vh" }}>
        <table className="w-full text-sm" data-testid="quick-entry-table">
          <thead className="bg-secondary sticky top-0 z-10">
            <tr>{["Dipendente", "Causale", "Note"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={3} className="px-3 py-8 text-center text-muted-foreground">Caricamento...</td></tr>}
            {!loading && filtrate.length === 0 && (
              <tr><td colSpan={3} className="px-3 py-8 text-center text-muted-foreground" data-testid="quick-empty">
                Nessun dipendente trovato
              </td></tr>
            )}
            {filtrate.map((r) => {
              const d = draft[r.employee_id] || {};
              return (
                <tr key={r.employee_id} className="border-t border-border" data-testid={`quick-row-${r.code}`}>
                  <td className="px-3 py-1.5 whitespace-nowrap w-[240px]">
                    <div className="text-[13px] font-semibold leading-tight">{r.last_name} {r.first_name}</div>
                    <div className="text-[10px] text-muted-foreground tabular-nums">{r.code} · {r.department}</div>
                  </td>
                  <td className="px-3 py-1.5 w-[260px]">
                    <div className="flex gap-1">
                      {STATUSES.map((s) => (
                        <button key={s} data-testid={`quick-status-${r.code}-${s}`}
                          onClick={() => setStatus(r, s)} title={s}
                          className={`h-7 w-9 rounded-sm text-[10px] font-bold border transition-colors duration-150
                            ${d.status === s ? "border-primary ring-2 ring-primary/40" : "border-border"} ${STATUS_META[s].cell}`}>
                          {STATUS_META[s].short}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-1.5">
                    <Input value={d.notes || ""} onChange={(e) => setNotes(r, e.target.value)}
                      className="h-8 rounded-sm" placeholder="Note" data-testid={`quick-notes-${r.code}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Il PDF giornaliero viene creato e archiviato da solo quando tutti i dipendenti attivi hanno
        una causale; se una causale viene tolta, il PDF di quel giorno torna in sospeso.
      </p>

      <Dialog open={!!turno} onOpenChange={(o) => !o && setTurno("")}>
        <DialogContent className="max-w-lg" data-testid="shift-text-dialog">
          <DialogHeader><DialogTitle>Lista turno — copia il testo</DialogTitle></DialogHeader>
          <textarea readOnly value={turno} rows={12} data-testid="shift-text-area"
            className="w-full rounded-sm border border-border bg-background p-2 font-mono text-xs"
            onFocus={(e) => e.target.select()} />
          <p className="text-xs text-muted-foreground">
            Il browser non ha consentito la copia automatica: tieni premuto sul testo e scegli Copia.
          </p>
        </DialogContent>
      </Dialog>

      <Dialog open={elencoApprovate} onOpenChange={setElencoApprovate}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="approved-requests-dialog">
          <DialogHeader>
            <DialogTitle>Richieste approvate — {itDate(day)} · {approvate?.total ?? 0}</DialogTitle>
          </DialogHeader>
          <div className="rounded-sm border border-border divide-y divide-border">
            {(approvate?.rows || []).map((r) => (
              <div key={r.id} className="px-3 py-2" data-testid={`approved-request-${r.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold">{r.employee_name}</div>
                  <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold bg-secondary text-secondary-foreground">
                    {TYPE_LABELS[r.type] || r.type}
                  </span>
                </div>
                <div className="text-[10px] text-muted-foreground tabular-nums">
                  {r.employee_code} · {r.department} · {itDate(r.date_from)} — {itDate(r.date_to)} · {r.days} gg
                </div>
                {r.notes && <div className="mt-1 text-xs text-muted-foreground">Note: {r.notes}</div>}
              </div>
            ))}
            {!(approvate?.rows || []).length && (
              <div className="px-3 py-6 text-sm text-muted-foreground" data-testid="approved-requests-empty">
                Nessuna richiesta approvata per questa data
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!elenco} onOpenChange={(o) => !o && setElenco(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="rollcall-list-dialog">
          <DialogHeader>
            <DialogTitle>{elenco?.label} — {itDate(day)} · {elencoRighe.length} dipendenti</DialogTitle>
          </DialogHeader>
          <div className="rounded-sm border border-border divide-y divide-border">
            {elencoRighe.map((r) => (
              <div key={r.employee_id} className="px-3 py-2 flex items-center justify-between gap-3"
                data-testid={`rollcall-list-${r.code}`}>
                <div>
                  <div className="text-sm font-semibold">{r.last_name} {r.first_name}</div>
                  <div className="text-[10px] text-muted-foreground tabular-nums">{r.code} · {r.department}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold
                  ${STATUS_META[draft[r.employee_id]?.status]?.cell || "bg-secondary"}`}>
                  {draft[r.employee_id]?.status || "NON REGISTRATO"}
                </span>
              </div>
            ))}
            {elencoRighe.length === 0 && (
              <div className="px-3 py-6 text-sm text-muted-foreground" data-testid="rollcall-list-empty">
                Nessun dipendente in questo stato
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!conferma} onOpenChange={(o) => !o && setConferma(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="apply-all-confirm">
          <DialogHeader><DialogTitle className="font-head">Causali già registrate</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {conferma?.gia} dipendenti hanno già una causale per questo giorno e {conferma?.vuoti} sono ancora senza.
            Vuoi lasciare invariate quelle già registrate oppure sostituirle con
            <b> {conferma ? STATUS_META[conferma.status].short : ""}</b>?
          </p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" className="rounded-sm" data-testid="apply-all-cancel"
              onClick={() => setConferma(null)}>Annulla</Button>
            <Button variant="outline" className="rounded-sm" data-testid="apply-all-keep"
              onClick={() => applica(conferma.status, true)}>
              Lascia quelle esistenti ({conferma?.vuoti} da compilare)
            </Button>
            <Button className="rounded-sm" data-testid="apply-all-overwrite"
              onClick={() => applica(conferma.status, false)}>
              Modifica tutti ({filtrate.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
